import { Injectable } from '@angular/core';
import { PscHttpService } from "./psc-http.service";
import { map, Observable } from "rxjs";
import { SolarCalculatorRequest } from "../common/models/analysis/solar-calculator-request";
import { CalculatorResultWrapper } from '../common/models/analysis/calculator-result-wrapper';
import { PowerPlant } from '../common/components/solar-calculation-result/model/power-plant';
import { ImplementationPlanRequest } from '../common/models/analysis/implementation-plan-request';
import { OfferRequest } from '../common/components/solar-calculation-result/model/offer-request';
import { OfferUpdateRequest } from '../common/models/analysis/offer-update-request';
import { CalculatorResult } from '../common/models/analysis/solar-calculator-result';
import { RecalculateRequest } from '../common/models/analysis/recalculate-request';
import { SavePlantResponse } from '../common/models/analysis/save-plant-response';
import { SimpleSolarCalculatorRequest } from '../common/models/analysis/simple-solar-calculator-request';
import { SimpleSolarCalculatorResult } from '../common/models/analysis/simple-solar-calculator-result';
import { EphzhbBasicAnalysisRequest } from '../common/models/analysis/ephzhbf-basic-analysis-request';
import { EphzhbExpenseData } from '../common/models/analysis/ephzhbf-expense-data';
import { SKIP_STATUS_CODES } from '../common/interceptors/error-response-handler.interceptor';
import { HttpContext } from '@angular/common/http';

export enum ProductionType {
  NREL = 'nrel',
  PVGIS = 'pvgis',
  PVSYS = 'pvsys',
  PVSOL = 'pvsol',
}

export enum ConsumptionType {
  MONTHLY = 'MONTHLY',
  HOURLY = 'HOURLY',
}

@Injectable({
  providedIn: 'root',
})
export class AnalysisService {

  baseEndpoint = 'analysis/';

  constructor(
    private httpService: PscHttpService,
  ) {
  }


  getAdvancedAnalysis(body: SolarCalculatorRequest, productionType: ProductionType, consumptionType: ConsumptionType, recalculate: boolean = false): Observable<CalculatorResultWrapper> {
    return this.httpService.post(`${ this.baseEndpoint }advance/${ productionType }/${ consumptionType.toLowerCase() }?recalculate=${ recalculate }`, body).pipe(
      map(result => new CalculatorResultWrapper(productionType, consumptionType, result, body)),
    );
  }

  getBasicAnalysis(body: SolarCalculatorRequest, productionType: ProductionType, consumptionType: ConsumptionType): Observable<CalculatorResultWrapper> {
    return this.httpService.post(`${ this.baseEndpoint }basic/${ productionType }/${ consumptionType.toLowerCase() }`, body).pipe(
      map(result => new CalculatorResultWrapper(productionType, consumptionType, result, body)),
    );
  }

  getBasicBankAnalysis(body: SimpleSolarCalculatorRequest): Observable<SimpleSolarCalculatorResult> {
    return this.httpService.post(`${ this.baseEndpoint }basic/bank/pvgis/monthly`, body);
  }

  /** EP HŽHB calculator — backend route uses uppercase PVGIS segment. */
  getEphzhbBasicAnalysis(body: EphzhbBasicAnalysisRequest): Observable<CalculatorResult> {
    return this.httpService.post(`${ this.baseEndpoint }basic/ephzhb/PVGIS/monthly`, body);
  }

  /**
   * EP HŽHB OMM lookup — proxies an external service, so it can take a few seconds.
   * 400 means an invalid OMM and is handled inline by the caller (skipped in the global error toast).
   */
  getEphzhbExpenseData(omm: string): Observable<EphzhbExpenseData> {
    return this.httpService.post(`${ this.baseEndpoint }basic/ephzhb/expense-data`, { omm }, {
      context: new HttpContext().set(SKIP_STATUS_CODES, [400]),
    });
  }

  recalculateSavedPlant(request: RecalculateRequest): Observable<CalculatorResult> {
    return this.httpService.post(`${ this.baseEndpoint }recalculate`, request);
  }


  getReport(formData: FormData) {
    return this.httpService.postReturnPdf(`${ this.baseEndpoint }report/pdf`, formData).pipe(
      map(result => result as unknown as Blob),
    );
  }

  savePowerPlant(formData: FormData): Observable<SavePlantResponse> {
    return this.httpService.post(`${ this.baseEndpoint }`, formData);
  }

  saveBasicPowerPlant(formData: FormData): Observable<SavePlantResponse> {
    return this.httpService.post(`${ this.baseEndpoint }basic`, formData);
  }

  updatePowerPlant(formData: FormData) {
    return this.httpService.put(`${ this.baseEndpoint }`, formData);
  }

  deletePowerPlant(plantId: string) {
    return this.httpService.delete(`${ this.baseEndpoint }${ plantId }`);
  }

  getSavedPowerPlants(): Observable<PowerPlant[]> {
    return this.httpService.get(`${ this.baseEndpoint }`);
  }

  getSavedPowerPlant(id: string): Observable<PowerPlant> {
    return this.httpService.get(`${ this.baseEndpoint }${ id }`);
  }

  saveImplementationPlan(request: ImplementationPlanRequest) {
    return this.httpService.put(`${ this.baseEndpoint }implementationPlan`, request);
  }

  getOfferRequests(): Observable<OfferRequest[]> {
    return this.httpService.get(`${ this.baseEndpoint }company`);
  }

  updateOfferRequest(offerUpdateRequest: OfferUpdateRequest) {
    return this.httpService.put(`${ this.baseEndpoint }offer`, offerUpdateRequest);
  }
}
